import type { ReactNode } from "react"
import type { ConfirmFn, ConfirmOptions } from "./use-confirm"
import { useTranslations } from "next-intl"
import React, { useCallback, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ConfirmContext } from "./confirm-context"

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    options: ConfirmOptions
    resolve?: (result: boolean) => void
    open: boolean
  }>({
        options: {},
        open: false,
      })
  const t = useTranslations()

  const confirm: ConfirmFn = useCallback(
    async options =>
      new Promise((resolve) => {
        setState({ options, resolve, open: true })
      }),
    [],
  )

  const handleClose = (result: boolean) => {
    state.resolve?.(result)
    setState(s => ({ ...s, open: false, resolve: undefined }))
  }

  return (
    <ConfirmContext value={confirm}>
      {children}
      <AlertDialog open={state.open}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {state.options.title || t("generic.are_you_sure")}
            </AlertDialogTitle>
            {state.options.description && (
              <AlertDialogDescription>
                {state.options.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {state.options.content}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleClose(false)}>
              {state.options.cancelText || t("generic.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleClose(true)}>
              {state.options.confirmText || t("generic.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext>
  )
}
